import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import type { BoxProps } from '@mui/material';

interface AnimatedSectionProps extends BoxProps {
  children: React.ReactNode;
  delay?: number;
}

export const AnimatedSection = ({ children, delay = 0, sx, ...props }: AnimatedSectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = ref.current;
    if (!currentRef) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
          observer.disconnect(); // Disconnect after first intersection
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(currentRef);

    return () => {
      observer.disconnect(); // Always disconnect on cleanup
    };
  }, [delay]);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};
